import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { Dna, Lock, Beaker } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { parseAxiosError } from '@/utils/axiosErrors';
import { DesignUploadCard } from '@/components/DesignUploadCard';
import { Phase1UploadCard } from '@/components/Phase1UploadCard';
import { Phase2UploadCard } from '@/components/Phase2UploadCard';
import { Phase3UploadCard } from '@/components/Phase3UploadCard';
import { Phase4UploadCard } from '@/components/Phase4UploadCard';
import SharedNav from '@/components/SharedNav';

const TABS = [  { key: 'diversity', label: 'Diversity Analysis', endpoint: '/analyze/diversity', desc: 'Marker Diversity, AMOVA, Nei Distance, Clustering', color: '#059669',
    format: 'Genotype, Population (optional), Marker1, Marker2, ... (coded 0/1/2)', locked: false },
  // Phase 1 — shared foundation for QTL / GWAS / GS pipelines
  // (Niranjan, Feb 2026): marker QC + phenotype distribution +
  // broad-sense H² + correlation heatmap. Numeric (0/1/2) genotype
  // matrix + phenotype CSV with optional Rep column.
  { key: 'phase1', label: 'Phase 1: Data QC & Phenotypic Analysis', desc: 'Marker QC + ANOVA-based heritability + correlation + normality testing', color: '#0891B2', locked: false },
  { key: 'qtl', label: 'Phase 2: QTL Mapping', desc: 'CIM-BIC + Churchill–Doerge permutation threshold + bootstrap stability', color: '#6366F1', locked: false },
  { key: 'gwas', label: 'Phase 3: GWAS', desc: 'EMMAX MLM (Q-matrix = PCs, K-matrix = VanRaden) + Bonferroni / FDR', color: '#8B5CF6', locked: false },
  { key: 'genomic-selection', label: 'Phase 4: Genomic Selection', desc: 'rrBLUP + GBLUP, 5-fold × 10-rep cross-validation', color: '#7C3AED', locked: false },
  // Sub-Batch 3AN (Niranjan, Feb 9 2026): visible polyploid roadmap
  // tab. The polyploid math module is on permanent hold (no R workers
  // in the container per /memory/POLYPLOID_INFRA_DECISION.md), but a
  // landing point for potato/sugarcane/sweet-potato/alfalfa breeders
  // is still owed. Tab is `locked` (no analysis upload, no backend
  // routing) but `roadmap: true` allows click navigation to a
  // dedicated roadmap card.
  { key: 'polyploid', label: 'Polyploid QTL Mapping', desc: 'Random-effect QTL models for potato 4x, sweet potato 6x, sugarcane, alfalfa, blueberry — in design', color: '#0F766E', locked: true, roadmap: true },
];

const wlInput = {
  padding: '9px 12px',
  border: '1px solid var(--border-color)',
  borderRadius: 8,
  fontSize: 13,
  fontFamily: 'inherit',
  color: 'var(--text-dark, #1F2937)',
  background: '#fff',
  outline: 'none',
};

export default function MolecularGenetics() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('diversity');
  const [analysisIds, setAnalysisIds] = useState({});
  const [exporting, setExporting] = useState(null);

  // Polyploid module waitlist form
  const [wlForm, setWlForm] = useState({ email: '', name: '', organisation: '', crop_interest: '', message: '' });
  const [wlSubmitting, setWlSubmitting] = useState(false);
  const [wlDone, setWlDone] = useState(false);

  const submitWaitlist = async (e) => {
    e.preventDefault();
    if (!wlForm.email.trim()) { toast.error('Email is required'); return; }
    setWlSubmitting(true);
    try {
      const resp = await axios.post(`${API}/waitlist`, wlForm);
      setWlDone(true);
      toast.success(resp.data?.message || "You're on the list.");
    } catch (err) {
      toast.error(parseAxiosError(err) || 'Could not submit — please try again.');
    } finally {
      setWlSubmitting(false);
    }
  };

  // BATCH-NEXT-3 (Niranjan, Feb 2026): support ?phase=N deep links
  // so the Phase 2 → Phase 4 "Promote to Fixed-QTL" handoff lands
  // directly on the Genomic Selection tab.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const phase = params.get('phase');
    const map = { '1': 'phase1', '2': 'qtl', '3': 'gwas', '4': 'genomic-selection' };
    if (phase && map[phase]) {
      setActiveTab(map[phase]);
    }
  }, []);

  const handleResult = (data, key) => {
    if (data?.id) setAnalysisIds(prev => ({ ...prev, [key]: data.id }));
  };

  const handleExport = async (analysisId, format) => {
    const k = `${analysisId}-${format}`;
    setExporting(k);
    toast.info(`Generating ${format.toUpperCase()}...`);
    try {
      const response = await axios.post(`${API}/generate-document/${analysisId}`, { format },
        // B-NEW-3 FIX-3A: 90 s timeout so a hung export doesn't
        // permanently disable the page's export buttons. Bumped from
        // 30 s in Sub-Batch 3W (Jun 2026) for large DOCX payloads.
        { withCredentials: true, responseType: 'blob', timeout: 90000 });
      const mimeMap = {
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        pdf: 'application/pdf',
      };
      const blobType = mimeMap[format] || 'application/octet-stream';
      const url = window.URL.createObjectURL(new Blob([response.data], { type: blobType }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BKAgriD_Report.${format}`);
      document.body.appendChild(link);
      link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded!`);
    } catch (e) {
      // B-NEW-2/3: blob 4xx/5xx may carry JSON detail; surface it.
      if (e?.code === 'ECONNABORTED') {
        toast.error('Export timed out. Please try again. If the issue persists, refresh the page (Ctrl+Shift+R).');
      } else {
        const detail = await parseAxiosError(e, 'Export failed.');
        toast.error(detail);
      }
    }
    finally { setExporting(null); }
  };

  const activeDesign = TABS.find(t => t.key === activeTab);

  return (
    <div className="min-h-screen" style={{ background: 'var(--page-bg)' }} data-testid="molecular-genetics-page">
      <SharedNav />

      <div className="max-w-[1320px] mx-auto px-6 page-enter" style={{ paddingTop: 32, paddingBottom: 48 }}>
        {/* Page Header */}
        <div className="mb-6" style={{ borderLeft: '4px solid var(--green-500)', paddingLeft: 16 }}>
          <h1 className="font-display flex items-center gap-2 flex-wrap" style={{ fontSize: 28, color: 'var(--green-900)' }}>
            <Dna size={24} style={{ color: 'var(--green-500)' }} />
            Molecular Genetics
            {/* Sub-Batch 3AI (Niranjan, Feb 9 2026): diploid-only
                badge. Protects potato/sweet-potato/sugarcane/alfalfa/
                blueberry breeders from silently running diploid math
                on polyploid dosage data. Polyploid module is in
                design (see /memory/POLYPLOID_QTL_METHODS_SPEC.md).
                The badge is paired with a strict backend dosage
                guard in routes/molecular_routes.py — UI signal
                and backend refusal both ship together. */}
            <span
              className="inline-flex items-center gap-1.5 font-body"
              style={{
                fontSize: 11,
                fontWeight: 700,
                padding: '4px 10px',
                borderRadius: 99,
                background: '#FEF3C7',
                border: '1.5px solid #F59E0B',
                color: '#92400E',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                marginLeft: 4,
              }}
              title="The Molecular Genetics module is currently diploid-only. Genotype matrices must use {0, 1, 2} dosage coding. Polyploid (4x potato, 6x sweet potato, sugarcane, etc.) support is in design — see roadmap."
              data-testid="molecular-diploid-only-badge"
            >
              <Lock size={10} /> Diploid only
            </span>
          </h1>
          <p className="font-body mt-1" style={{ fontSize: 15, color: 'var(--text-medium)' }}>
            Analyze molecular marker data for diversity, QTL mapping, GWAS, and genomic selection.
          </p>
          {/* Honest-mode explanation strip below the title so the
              constraint is visible without hovering. Aimed at PhD
              breeders — explains the math, not just "not supported". */}
          <p
            className="font-body mt-2 leading-relaxed"
            style={{
              fontSize: 12,
              color: '#92400E',
              background: '#FEF3C7',
              borderLeft: '3px solid #F59E0B',
              padding: '8px 12px',
              borderRadius: 6,
              maxWidth: 880,
            }}
            data-testid="molecular-diploid-only-explainer"
          >
            <strong>Diploid pipeline.</strong> All four phases (QC, QTL, GWAS, Genomic Selection) assume
            genotype dosages in <code style={{ fontFamily: 'monospace', background: '#FDE68A', padding: '0 4px', borderRadius: 3 }}>{'{0, 1, 2}'}</code>{' '}
            and apply diploid recombination math (Kosambi / Haldane). Polyploid dosage data
            (potato 4x, sweet potato 6x, sugarcane, alfalfa, blueberry) will be{' '}
            <strong>rejected at upload</strong> with a refusal that links to the polyploid module roadmap.
            This is deliberate — polyploid math is genuinely different (random-effect QTL models,
            score-statistic thresholds, double-reduction estimation), and silently running diploid math
            on polyploid data produces numerically valid but biologically incorrect results.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1.5 p-1.5 bg-white rounded-xl mb-6" style={{ border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)', width: 'fit-content' }}>
          {TABS.map(t => {
            const isActive = activeTab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => { if (!t.locked || t.roadmap) setActiveTab(t.key); }}
                disabled={t.locked && !t.roadmap}
                className="flex items-center gap-1.5 font-body whitespace-nowrap btn-press transition-all"
                style={{
                  padding: '8px 18px', borderRadius: 'var(--radius-md)',
                  fontSize: 13, fontWeight: 500,
                  background: t.locked ? (isActive && t.roadmap ? 'white' : 'transparent') : (isActive ? 'white' : 'transparent'),
                  color: t.locked ? (isActive && t.roadmap ? 'var(--green-900)' : 'var(--text-light)') : (isActive ? 'var(--green-900)' : 'var(--text-medium)'),
                  boxShadow: isActive && (!t.locked || t.roadmap) ? '0 1px 6px rgba(0,0,0,0.12)' : 'none',
                  opacity: t.locked && !t.roadmap ? 0.5 : 1,
                  cursor: t.locked && !t.roadmap ? 'not-allowed' : 'pointer',
                  border: t.locked ? '1px dashed var(--border-color)' : '1px solid transparent',
                }}
                data-testid={`mol-tab-${t.key}`}
              >
                {t.locked ? <Lock size={12} /> : <Beaker size={13} />}
                {t.label}
                {t.locked && (
                  <span className="font-body" style={{ fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 99, background: '#F1F5F9', color: 'var(--text-light)' }}>
                    Coming Soon
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Active Tab Content */}
        {activeDesign && activeDesign.locked && activeDesign.roadmap && (
          <div className="bg-white rounded-2xl fade-in" style={{ border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)' }} data-testid={`mol-roadmap-${activeDesign.key}`}>
            <div className="p-6 pb-0">
              <div className="flex items-center gap-3 mb-5">
                <div className="flex items-center justify-center font-body" style={{
                  width: 44, height: 44, borderRadius: 10,
                  background: activeDesign.color, color: 'white', fontSize: 18, fontWeight: 700,
                }}>
                  {activeDesign.label.charAt(0)}
                </div>
                <div>
                  <div className="font-body flex items-center gap-2" style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-dark)' }}>
                    {activeDesign.label}
                    <span className="font-body" style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 99, background: '#FEF3C7', color: '#92400E', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                      In Design — Not Yet Live
                    </span>
                  </div>
                  <div className="font-body" style={{ fontSize: 14, color: 'var(--text-medium)' }}>{activeDesign.desc}</div>
                </div>
              </div>
            </div>
            <div className="px-6 pb-6">
              {/* Sub-Batch 3AN roadmap card. Source-of-truth: see
                  /app/memory/POLYPLOID_QTL_METHODS_SPEC.md and
                  /app/memory/POLYPLOID_INFRA_DECISION.md. */}
              <div className="font-body" style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 12, padding: 20, marginBottom: 16 }}>
                <h3 style={{ fontSize: 15, fontWeight: 600, color: '#14532D', marginBottom: 8 }}>Who is this for?</h3>
                <p style={{ fontSize: 14, color: '#166534', lineHeight: 1.6 }}>
                  Breeders working with <strong>polyploid</strong> field crops where each genotype carries more than two
                  homologous chromosomes — potato (4x), sweet potato (6x), sugarcane (8–12x), alfalfa (4x),
                  blueberry (4x), strawberry (8x). The standard diploid {`{0, 1, 2}`} dosage coding does
                  not apply; instead each marker scores allelic dosage on the {`{0, 1, … 2k}`} scale where{' '}
                  <em>k</em> is the ploidy level.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div style={{ background: 'white', border: '1px solid var(--border-color)', borderRadius: 10, padding: 16 }}>
                  <h4 className="font-body" style={{ fontSize: 13, fontWeight: 600, color: 'var(--green-900)', marginBottom: 6 }}>Why the diploid pipeline cannot be reused</h4>
                  <ul className="font-body" style={{ fontSize: 13, color: 'var(--text-medium)', lineHeight: 1.7, paddingLeft: 18, listStyle: 'disc' }}>
                    <li>Polyploid recombination math is not Kosambi/Haldane — preferential pairing and double reduction modify expected linkage frequencies</li>
                    <li>QTL effects are typically modelled as random (BLUP) rather than fixed, because allelic configurations within a locus are usually unobservable</li>
                    <li>Significance thresholds use score-statistic distributions, not the Churchill–Doerge interval permutation</li>
                  </ul>
                </div>
                <div style={{ background: 'white', border: '1px solid var(--border-color)', borderRadius: 10, padding: 16 }}>
                  <h4 className="font-body" style={{ fontSize: 13, fontWeight: 600, color: 'var(--green-900)', marginBottom: 6 }}>Planned method stack</h4>
                  <ul className="font-body" style={{ fontSize: 13, color: 'var(--text-medium)', lineHeight: 1.7, paddingLeft: 18, listStyle: 'disc' }}>
                    <li>Dosage QC for {`{0…2k}`} marker matrices + ploidy auto-detection</li>
                    <li>Random-effect QTL scan (mixed model) with score-statistic threshold</li>
                    <li>Double-reduction-aware allelic frequency estimation</li>
                    <li>Interval-mapping LOD profile + bootstrap stability on the polyploid model</li>
                  </ul>
                </div>
              </div>

              <div className="font-body" style={{ background: '#FEF3C7', borderLeft: '3px solid #F59E0B', borderRadius: 6, padding: 14, fontSize: 13, color: '#92400E', lineHeight: 1.6 }}>
                <strong>Honest status:</strong> the polyploid module is currently <strong>on hold</strong>{' '}
                pending compute infrastructure (random-effect QTL mixed models at scale need a job queue
                and worker pool that BKAgriD does not yet operate — see{' '}
                <code style={{ fontFamily: 'monospace', background: '#FDE68A', padding: '0 4px', borderRadius: 3 }}>/memory/POLYPLOID_INFRA_DECISION.md</code>).
                If your trial is polyploid and this gap is blocking you, contact us at{' '}
                <a href="mailto:bkagrid@gmail.com" style={{ color: '#92400E', textDecoration: 'underline' }} data-testid="mol-polyploid-contact-email">bkagrid@gmail.com</a>{' '}
                — we prioritize based on direct breeder demand and would rather hear from real users than
                guess at it.
              </div>

              {/* Validated demonstration — allohexaploid wheat (Module 4) + autotetraploid potato (Module 5) */}
              <div className="font-body" style={{ background: 'var(--green-100, #D8F3DC)', borderLeft: '3px solid var(--green-500)', borderRadius: 6, padding: 14, fontSize: 13, color: 'var(--green-900)', lineHeight: 1.6, marginTop: 12 }} data-testid="mol-polyploid-validated-demo">
                <strong>✓ Validated demonstrations available — two polyploid modes.</strong>{' '}
                While the full production compute pipeline is parked, BKAgriD validates BOTH polyploid
                inheritance modes against published international data:{' '}
                <strong>Module 4</strong> — the <em>disomic</em> allohexaploid case (bread wheat, CIMMYT-co-authored
                Gao et al. 2015), recovering the flagship 6A QTL across the A/B/D subgenomes; and{' '}
                <strong>Module 5</strong> — the genuinely <em>polysomic</em> AUTOtetraploid case (potato, dosage {`{0…4}`},
                B2721 Atlantic × B1829-5, da Silva Pereira et al. 2020), recovering the chromosome-5 maturity QTL
                from a tetrasomic simulation.{' '}
                <button
                  onClick={() => navigate('/validation')}
                  className="btn-press"
                  style={{ background: 'none', border: 'none', color: 'var(--green-500)', textDecoration: 'underline', cursor: 'pointer', padding: 0, fontSize: 13, fontWeight: 600 }}
                  data-testid="mol-polyploid-goto-validation"
                >
                  View Modules 4 &amp; 5 in the Validation Dashboard →
                </button>
              </div>

              {/* Beta pipeline — run a real polysomic scan now */}
              <div style={{ marginTop: 12, background: 'white', border: '2px solid var(--green-500)', borderRadius: 10, padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }} data-testid="mol-polyploid-pipeline-cta">
                <div style={{ maxWidth: 640 }}>
                  <div className="font-body" style={{ fontSize: 14, fontWeight: 700, color: 'var(--green-900)', marginBottom: 2 }}>
                    🧬 Run a real autotetraploid QTL scan now (Beta)
                  </div>
                  <div className="font-body" style={{ fontSize: 13, color: 'var(--text-medium)', lineHeight: 1.6 }}>
                    Upload your own tetraploid dosage {`{0…4}`} data (or try a sample) and get a genome-wide
                    dosage QTL scan with a permutation threshold — running in-platform at feasible scale.
                  </div>
                </div>
                <button
                  onClick={() => navigate('/polyploid-qtl')}
                  className="btn-press"
                  style={{ padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 700, background: 'var(--green-500)', color: 'white', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}
                  data-testid="mol-polyploid-goto-pipeline"
                >
                  Open QTL Pipeline →
                </button>
              </div>

              {/* Waitlist / express-interest form */}
              <div style={{ marginTop: 16, background: 'white', border: '1px solid var(--border-color)', borderRadius: 10, padding: 18 }} data-testid="mol-polyploid-waitlist">
                <h4 className="font-body" style={{ fontSize: 14, fontWeight: 700, color: 'var(--green-900)', marginBottom: 4 }}>
                  Breeding polyploids? Join the waitlist
                </h4>
                <p className="font-body" style={{ fontSize: 13, color: 'var(--text-medium)', marginBottom: 14, lineHeight: 1.6 }}>
                  We prioritise the polysomic autopolyploid compute module (potato&nbsp;4x, sweet potato&nbsp;6x,
                  sugarcane, alfalfa, blueberry) by direct breeder demand. Tell us your crop and we'll reach out
                  the moment it's ready.
                </p>
                {wlDone ? (
                  <div className="font-body" style={{ background: 'var(--green-100, #D8F3DC)', borderRadius: 8, padding: 16, color: 'var(--green-900)', fontSize: 14, fontWeight: 600 }} data-testid="mol-polyploid-waitlist-success">
                    ✓ You're on the list. We'll be in touch — thank you for shaping the roadmap.
                  </div>
                ) : (
                  <form onSubmit={submitWaitlist} data-testid="mol-polyploid-waitlist-form">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 12 }}>
                      <input
                        type="email" required placeholder="Email address *"
                        value={wlForm.email}
                        onChange={(e) => setWlForm({ ...wlForm, email: e.target.value })}
                        style={wlInput} data-testid="mol-polyploid-waitlist-email"
                      />
                      <input
                        type="text" placeholder="Your name"
                        value={wlForm.name}
                        onChange={(e) => setWlForm({ ...wlForm, name: e.target.value })}
                        style={wlInput} data-testid="mol-polyploid-waitlist-name"
                      />
                      <input
                        type="text" placeholder="Organisation / institute"
                        value={wlForm.organisation}
                        onChange={(e) => setWlForm({ ...wlForm, organisation: e.target.value })}
                        style={wlInput} data-testid="mol-polyploid-waitlist-org"
                      />
                      <select
                        value={wlForm.crop_interest}
                        onChange={(e) => setWlForm({ ...wlForm, crop_interest: e.target.value })}
                        style={wlInput} data-testid="mol-polyploid-waitlist-crop"
                      >
                        <option value="">Crop / ploidy of interest…</option>
                        <option value="Potato (autotetraploid, 4x)">Potato (autotetraploid, 4x)</option>
                        <option value="Sweet potato (hexaploid, 6x)">Sweet potato (hexaploid, 6x)</option>
                        <option value="Sugarcane (high polyploid)">Sugarcane (high polyploid)</option>
                        <option value="Alfalfa (autotetraploid, 4x)">Alfalfa (autotetraploid, 4x)</option>
                        <option value="Blueberry (tetraploid, 4x)">Blueberry (tetraploid, 4x)</option>
                        <option value="Other polyploid">Other polyploid</option>
                      </select>
                    </div>
                    <textarea
                      placeholder="Anything about your population or trait we should know? (optional)"
                      value={wlForm.message}
                      onChange={(e) => setWlForm({ ...wlForm, message: e.target.value })}
                      rows={3}
                      style={{ ...wlInput, width: '100%', resize: 'vertical', marginBottom: 12 }}
                      data-testid="mol-polyploid-waitlist-message"
                    />
                    <button
                      type="submit" disabled={wlSubmitting}
                      className="btn-press"
                      style={{ padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: 'var(--green-500)', color: 'white', border: 'none', cursor: wlSubmitting ? 'wait' : 'pointer', opacity: wlSubmitting ? 0.7 : 1 }}
                      data-testid="mol-polyploid-waitlist-submit"
                    >
                      {wlSubmitting ? 'Submitting…' : 'Join the waitlist'}
                    </button>
                  </form>
                )}
              </div>

              {/* Quick visible jump back to the working diploid pipeline */}
              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setActiveTab('qtl')}
                  className="font-body btn-press"
                  style={{ padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 500, background: 'var(--green-500)', color: 'white', border: 'none', cursor: 'pointer' }}
                  data-testid="mol-polyploid-goto-diploid-qtl"
                >
                  Back to Diploid QTL Mapping (Phase 2)
                </button>
              </div>
            </div>
          </div>
        )}

        {activeDesign && !activeDesign.locked && (
          <div className="bg-white rounded-2xl fade-in" style={{ border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)' }}>
            <div className="p-6 pb-0">
              <div className="flex items-center gap-3 mb-5" data-testid={`mol-title-${activeDesign.key}`}>
                <div className="flex items-center justify-center font-body" style={{
                  width: 44, height: 44, borderRadius: 10,
                  background: activeDesign.color, color: 'white', fontSize: 18, fontWeight: 700,
                }}>
                  {activeDesign.label.charAt(0)}
                </div>
                <div>
                  <div className="font-body" style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-dark)' }}>{activeDesign.label}</div>
                  <div className="font-body" style={{ fontSize: 14, color: 'var(--text-medium)' }}>{activeDesign.desc}</div>
                </div>
              </div>
            </div>
            <div className="px-6 pb-6">
              {activeDesign.key === 'phase1' ? (
                <Phase1UploadCard
                  onResult={(data) => handleResult(data, 'phase1')}
                  analysisId={analysisIds['phase1']}
                  exporting={exporting}
                  onExport={handleExport}
                />
              ) : activeDesign.key === 'qtl' ? (
                <Phase2UploadCard
                  onResult={(data) => handleResult(data, 'qtl')}
                  analysisId={analysisIds['qtl']}
                  exporting={exporting}
                  onExport={handleExport}
                />
              ) : activeDesign.key === 'gwas' ? (
                <Phase3UploadCard
                  onResult={(data) => handleResult(data, 'gwas')}
                  analysisId={analysisIds['gwas']}
                  exporting={exporting}
                  onExport={handleExport}
                />
              ) : activeDesign.key === 'genomic-selection' ? (
                <Phase4UploadCard
                  onResult={(data) => handleResult(data, 'genomic-selection')}
                  analysisId={analysisIds['genomic-selection']}
                  exporting={exporting}
                  onExport={handleExport}
                />
              ) : (
                <DesignUploadCard
                  key={activeDesign.key}
                  design={activeDesign}
                  onResult={(data) => handleResult(data, activeDesign.key)}
                  analysisId={analysisIds[activeDesign.key]}
                  exporting={exporting}
                  onExport={handleExport}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
